import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { connectApiToAuth } from '@/api';
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext';
import { toAuthError } from './errors';
import type { AuthAdapter, AuthUser } from './types';

interface AuthProviderProps {
  adapter: AuthAdapter;
  /** Called after sign-out (e.g. to clear cached server data). */
  onSignedOut?: () => void;
  children: ReactNode;
}

export function AuthProvider({ adapter, onSignedOut, children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const signedIn = useRef(false);

  useEffect(() => {
    const unsubscribe = adapter.onAuthStateChanged((next) => {
      setUser(next);
      setStatus(next ? 'authenticated' : 'unauthenticated');
      // Only an actual sign-out clears server data: public pages (marketplace)
      // fetch before the initial "no session" answer and must keep their data.
      if (!next && signedIn.current) onSignedOut?.();
      signedIn.current = Boolean(next);
    });
    connectApiToAuth({
      getAccessToken: (options) => adapter.getAccessToken(options),
      onUnauthorized: () => void adapter.signOut(),
    });
    return unsubscribe;
  }, [adapter, onSignedOut]);

  const value = useMemo<AuthContextValue>(() => {
    const wrap =
      <Args extends unknown[], R>(fn: (...args: Args) => Promise<R>) =>
      async (...args: Args) => {
        try {
          return await fn(...args);
        } catch (error) {
          throw toAuthError(error);
        }
      };

    return {
      status,
      user,
      providerId: adapter.id,
      signInWithGoogle: wrap((options?: { returnTo?: string }) =>
        adapter.signInWithGoogle(options),
      ),
      completeRedirectSignIn: wrap((code: string, appVerifier?: string) =>
        adapter.completeRedirectSignIn(code, appVerifier),
      ),
      signInAsDevUser: wrap((email: string) => adapter.signInAsDevUser(email)),
      signOut: wrap(() => adapter.signOut()),
      getAccessToken: (options) => adapter.getAccessToken(options),
    };
  }, [adapter, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
